import React, { Component } from 'react';
import { arrayOf, func, node, number, shape, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import BoatSizeFilterForm from './BoatSizeFilterForm/BoatSizeFilterForm';

import css from './BoatSizeRangeFilterPlain.module.css';

const RADIX = 10;

const getBoatSizeQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'pub_maxBoatSize';
};

// Parse value, which should look like "0,1000"
const parse = boatSizeRange => {
  const [minBoatSize, maxBoatSize] = !!boatSizeRange
    ? boatSizeRange.split(',').map(v => Number.parseInt(v, RADIX))
    : [];
  // Note: we compare to null, because 0 as minBoatSize is falsy in comparisons.
  return !!boatSizeRange && minBoatSize != null && maxBoatSize != null
    ? { minBoatSize, maxBoatSize }
    : null;
};

// Format value, which should look like { minBoatSize, maxBoatSize }
const format = (range, queryParamName) => {
  const { minBoatSize, maxBoatSize } = range || {};
  // Note: we compare to null, because 0 as minBoatSize is falsy in comparisons.
  const value = minBoatSize != null && maxBoatSize != null ? `${minBoatSize},${maxBoatSize}` : null;
  return { [queryParamName]: value };
};

class BoatSizeRangeFilterPlainComponent extends Component {
  constructor(props) {
    super(props);
    this.state = { isOpen: true };

    this.handleChange = this.handleChange.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.toggleIsOpen = this.toggleIsOpen.bind(this);
  }

  handleChange(values) {
    const { onSubmit, queryParamNames } = this.props;
    const boatSizeQueryParamName = getBoatSizeQueryParamName(queryParamNames);
    onSubmit(format(values, boatSizeQueryParamName));
  }

  handleClear() {
    const { onSubmit, queryParamNames } = this.props;
    const boatSizeQueryParamName = getBoatSizeQueryParamName(queryParamNames);
    onSubmit(format(null, boatSizeQueryParamName));
  }

  toggleIsOpen() {
    this.setState(prevState => ({ isOpen: !prevState.isOpen }));
  }

  render() {
    const {
      rootClassName,
      className,
      id,
      label,
      queryParamNames,
      initialValues,
      min,
      max,
      step,
      intl,
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);

    const boatSizeQueryParam = getBoatSizeQueryParamName(queryParamNames);
    const initialBoatSize = initialValues ? parse(initialValues[boatSizeQueryParam]) : {};
    const { minBoatSize, maxBoatSize } = initialBoatSize || {};

    const hasValue = value => value != null;
    const hasInitialValues = initialValues && hasValue(minBoatSize) && hasValue(maxBoatSize);

    const labelClass = hasInitialValues ? css.filterLabelSelected : css.filterLabel;
    const labelText = hasInitialValues
      ? intl.formatMessage({ id: 'BoatSizeFilterForm.labelSelectedPlain' })
      : label
      ? label
      : intl.formatMessage({ id: 'BoatSizeFilterForm.label' });

    return (
      <div className={classes}>
        <div className={labelClass}>
          <button type="button" className={css.labelButton} onClick={this.toggleIsOpen}>
            <span className={labelClass}>{labelText}</span>
          </button>
          <button type="button" className={css.clearButton} onClick={this.handleClear}>
            <FormattedMessage id={'BoatSizeFilterForm.clear'} />
          </button>
        </div>
        <div className={css.formWrapper}>
          <BoatSizeFilterForm
            id={id}
            initialValues={
              hasInitialValues ? initialBoatSize : { minBoatSize: min, maxBoatSize: max }
            }
            onChange={this.handleChange}
            intl={intl}
            contentRef={node => {
              this.filterContent = node;
            }}
            min={min}
            max={max}
            step={step}
            liveEdit
            isOpen={this.state.isOpen}
          />
        </div>
      </div>
    );
  }
}

BoatSizeRangeFilterPlainComponent.defaultProps = {
  rootClassName: null,
  className: null,
  initialValues: null,
  step: number,
};

BoatSizeRangeFilterPlainComponent.propTypes = {
  rootClassName: string,
  className: string,
  id: string.isRequired,
  label: node,
  queryParamNames: arrayOf(string).isRequired,
  onSubmit: func.isRequired,
  initialValues: shape({
    boatSize: string,
  }),
  min: number.isRequired,
  max: number.isRequired,
  step: number,

  // form injectIntl
  intl: intlShape.isRequired,
};

const BoatSizeRangeFilterPlain = injectIntl(BoatSizeRangeFilterPlainComponent);

export default BoatSizeRangeFilterPlain;
